import {
  AfterViewChecked,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  inject,
} from '@angular/core';

const NUMERIC_CELL = /^[-+(]?[$€£]?\s?\d[\d\s.,:/-]*\)?%?$/;
const NUMERIC_COLUMN_RATIO = 0.6;
const OVERFLOW_TOLERANCE_PX = 4;

@Directive({
  selector: '[qtAlloTable]',
  standalone: true,
})
export class AlloTableDirective implements AfterViewChecked, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly observed = new WeakSet<Element>();
  private readonly resizeObserver = new ResizeObserver(entries => {
    for (const entry of entries) {
      this.syncOverflow(entry.target as HTMLElement);
    }
  });

  ngAfterViewChecked(): void {
    this.zone.runOutsideAngular(() => this.enhance());
  }

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
  }

  private enhance(): void {
    const blocks =
      this.host.nativeElement.querySelectorAll<HTMLElement>('.qt-table-block');

    for (const block of Array.from(blocks)) {
      if (this.observed.has(block)) {
        continue;
      }

      const table = block.querySelector('table');
      const scroller = block.querySelector<HTMLElement>('.table-wrapper');
      if (!table || !scroller || !table.tBodies[0]?.rows.length) {
        continue;
      }

      this.observed.add(block);
      this.markNumericColumns(table);
      this.resizeObserver.observe(scroller);
      scroller.addEventListener('scroll', () => this.syncOverflow(scroller), {
        passive: true,
      });
      this.syncOverflow(scroller);
    }
  }

  private markNumericColumns(table: HTMLTableElement): void {
    const bodyRows = Array.from(table.tBodies[0].rows);
    const columnCount = table.tHead?.rows[0]?.cells.length ?? 0;

    for (let column = 0; column < columnCount; column++) {
      const values = bodyRows
        .map(row => row.cells[column]?.textContent?.trim() ?? '')
        .filter(value => value.length > 0 && value !== '—' && value !== '-');
      if (!values.length) {
        continue;
      }

      const numeric = values.filter(value => NUMERIC_CELL.test(value)).length;
      if (numeric / values.length < NUMERIC_COLUMN_RATIO) {
        continue;
      }

      const headerCell = table.tHead?.rows[0]?.cells[column];
      headerCell?.classList.add('qt-col--num');
      for (const row of bodyRows) {
        row.cells[column]?.classList.add('qt-col--num');
      }
    }
  }

  private addRowNumbers(table: HTMLTableElement): void {
    const headRow = table.tHead?.rows[0];
    if (!headRow || headRow.querySelector('.qt-col--rownum')) {
      return;
    }

    const th = document.createElement('th');
    th.className = 'qt-col--rownum';
    th.setAttribute('aria-label', 'Row number');
    headRow.insertBefore(th, headRow.cells[0] ?? null);

    Array.from(table.tBodies[0].rows).forEach((row, index) => {
      const td = document.createElement('td');
      td.className = 'qt-col--rownum';
      td.textContent = String(index + 1);
      row.insertBefore(td, row.cells[0] ?? null);
    });
  }

  private removeRowNumbers(table: HTMLTableElement): void {
    for (const cell of Array.from(table.querySelectorAll('.qt-col--rownum'))) {
      cell.remove();
    }
  }

  private syncOverflow(scroller: HTMLElement): void {
    const block = scroller.closest<HTMLElement>('.qt-table-block');
    const table = scroller.querySelector('table');
    if (!block || !table) {
      return;
    }

    const overflows =
      scroller.scrollWidth - scroller.clientWidth > OVERFLOW_TOLERANCE_PX;
    block.classList.toggle('is-scrollable', overflows);

    if (overflows) {
      this.addRowNumbers(table);
    } else {
      this.removeRowNumbers(table);
    }

    const label = block.querySelector<HTMLElement>('.qt-table-block__count');
    if (!label) {
      return;
    }

    const total = this.dataColumnCount(table);
    label.textContent = overflows
      ? `${this.visibleColumnCount(scroller, table)} of ${total} columns`
      : `${total} columns`;
  }

  private dataColumnCount(table: HTMLTableElement): number {
    const cells = table.tHead?.rows[0]?.cells;
    if (!cells) {
      return 0;
    }
    return Array.from(cells).filter(
      cell => !cell.classList.contains('qt-col--rownum')
    ).length;
  }

  private visibleColumnCount(
    scroller: HTMLElement,
    table: HTMLTableElement
  ): number {
    const cells = table.tHead?.rows[0]?.cells;
    if (!cells) {
      return 0;
    }

    const left = scroller.scrollLeft;
    const right = left + scroller.clientWidth;
    let visible = 0;

    for (const cell of Array.from(cells)) {
      if (cell.classList.contains('qt-col--rownum')) {
        continue;
      }
      const start = cell.offsetLeft;
      const end = start + cell.offsetWidth;
      const shown = Math.min(end, right) - Math.max(start, left);
      if (shown > 0 && shown >= cell.offsetWidth / 2) {
        visible++;
      }
    }

    return visible;
  }
}
